import { useEffect, useState } from "react";
import { Trans } from "react-i18next";
import { t } from "i18next";
import classNames from "classnames";
import { Heading } from "../../../ui/components/Heading";
import { Button } from "@unleashed/components/ui/Button";
import { faUpload } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import FileUploadModal from "../../../forms/components/FileUploadModal";
import {
  WingmanAdminDocument,
  WingmanAdminDocumentIndentifier,
  WingmanAdminDocumentToReplace,
} from "../../models/WingmanAdminDocument";
import WingmanAdminUploadTable from "../../components/WingmanAdminUploadTable";
import {
  UploadRequest,
  useUploadWingmanAdminDocumentsMutation,
  useDeleteWingmanAdminDocumentMutation,
  useLazyCheckIfDocumentNameExistsQuery,
  useDocumentReplaceMutation,
  DocumentReplaceRequest,
  useUpdateWingmanAdminDocumentBrandsMutation,
  useUpdateWingmanAdminDocumentRolesMutation,
  useUpdateWingmanAdminDocumentViewableMutation,
  UpdateRolesRequest,
  UpdateBrandsRequest,
  UpdateViewableRequest,
  useGetWingmanAdminDocumentsByBrandQuery,
} from "../../api/wingmanAdminAPI";
import WingmanAdminUploadWarning from "../../components/WingmanAdminUploadWarning";
import { UploadStatus } from "../../../locations/features/migrations/enums/MigrationStatus";
import WingmanAdminModal from "../../components/WingmanAdminModal";
import WingmanAdminConfirmDeleteModal from "../../components/WingmanAdminConfirmDeleteModal";
import { PermissionGuard } from "../../../core/components/PermissionGuard";
import { UserPermissions } from "../../../core/models/UserPermissions";
import {
  brandsArrayForAdminDocumentsAPI,
  checkIfAllFieldsAreFilled,
  createReplacePayload,
  createUpdateBrandsPayload,
  createUpdateRolesPayload,
  createUpdateViewablePayload,
  createUploadPayload,
  mapBrand,
  mapRoleBrandToStrArr,
  removeExistingDocumentsFromPayload,
} from "../../utils/helpers";
import WingmanAdminConfirmReplaceModal from "../../components/WingmanAdminConfirmReplaceModal";
import { useSlugs } from "../../../core/hooks/useSlugs";
import WingmanAdminErrorModal from "../../components/WingmanAdminErrorModal";
import { useAdminSelector } from "../../../../stores/AdminStore";
import MultipleFileUploadModal from "../../../forms/components/MultipleFileUploadModal";

export const WingmanAdmin = () => {
  // Authorized Brands
  const brandPermissions = useAdminSelector((s) => s.auth.permissions);
  const [autorizedBrands, setAutorizedBrands] = useState<string[]>([]);

  useEffect(() => {
    if (brandPermissions) {
      setAutorizedBrands(
        brandPermissions
          .map((p) => p.urlSlug)
          .filter((urlSlug): urlSlug is string => urlSlug !== undefined)
          .map((urlSlug) => mapBrand(urlSlug))
      );
    }
  }, [brandPermissions]);

  // Fetch Documents According to Brand
  const { brandSlug } = useSlugs();
  const [documents, setDocuments] = useState<WingmanAdminDocument[]>([]);
  const { data: brandDocuments, refetch: refetchDocumentByBrand } =
    useGetWingmanAdminDocumentsByBrandQuery(
      { brand: mapBrand(brandSlug), authorizedBrands: autorizedBrands },
      {
        skip: !brandSlug, // Skip the query if brandSlug is not defined
      }
    );

  useEffect(() => {
    if (brandDocuments) {
      setDocuments(mapRoleBrandToStrArr(brandDocuments));
    }
  }, [brandDocuments]);

  // Document Table Ctrls
  const [beginImport, setBeginImport] = useState<boolean>(false);
  const handleCancel = () => setBeginImport(false);
  const handleBeginImport = () => setBeginImport(true);

  // Modals
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [errorModalMssg, setErrorModalMssg] = useState<string>("");
  const handleErrorContinue = () => setShowErrorModal(false);
  const [showUploadModalMultiple, setShowUploadModalMultiple] =
    useState<boolean>(false);
  const [showReplaceModal, setShowReplaceModal] = useState<boolean>(false);
  const [showConfirmationModal, setConfirmationModal] =
    useState<boolean>(false);
  const [confirmationModalMssg, setConfirmationModalMssg] =
    useState<string>("");
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] =
    useState<boolean>(false);
  const [showConfirmReplaceModal, setShowConfirmReplaceModal] =
    useState<boolean>(false);
  const [confirmationReplaceModalMssg, setConfirmationReplaceModalMssg] =
    useState<string>("");
  const handleShowReplaceModal = (shouldShow: boolean) =>
    setShowReplaceModal(shouldShow);
  const handleShowUploadModalMultiple = (shouldShow: boolean) =>
    setShowUploadModalMultiple(shouldShow);

  // Files
  const [files, setFiles] = useState<File[]>([]);
  const [fileToDelete, setFileToDelete] = useState<string>("");
  const [replacingFile, setReplacingFile] = useState<boolean>();
  const [pendingUploadPayload, setPendingUploadPayload] =
    useState<UploadRequest | null>(null);
  const [fileToReplace, setFileToReplace] =
    useState<WingmanAdminDocumentIndentifier | null>();
  const [payloadOfFileToDelete, setPayloadOfFileToDelete] =
    useState<WingmanAdminDocumentIndentifier>();

  // API Queries and Mutations
  const [replaceDocument] = useDocumentReplaceMutation();
  const [deleteDocument] = useDeleteWingmanAdminDocumentMutation();
  const [updateRoles] = useUpdateWingmanAdminDocumentRolesMutation();
  const [uploadDocuments] = useUploadWingmanAdminDocumentsMutation();
  const [updateBrands] = useUpdateWingmanAdminDocumentBrandsMutation();
  const [updateViewable] = useUpdateWingmanAdminDocumentViewableMutation();
  const [checkIfDocumentNameExists] = useLazyCheckIfDocumentNameExistsQuery();

  const handleImport = async () => {
    const filesToImport = documents.filter(
      (d) => d.uploadStatus === UploadStatus.Ready
    );
    const fileNames = filesToImport.map((d) => d.Filename);

    const payload = createUploadPayload(filesToImport, files);

    const result = await checkIfDocumentNameExists(fileNames as string[]);
    const { data: doesExist, error } = result;

    if (error) {
      return;
    }

    if (doesExist !== undefined && doesExist.length > 0) {
      const documentList = doesExist.join(", ");
      const filteredPayload = removeExistingDocumentsFromPayload(
        payload,
        doesExist
      );
      setPendingUploadPayload(filteredPayload);
      setConfirmationModal(true);
      setConfirmationModalMssg(
        `${t("The following document(s) already exist")}: ${documentList}. ${t(
          "These documents will not be uploaded"
        )}`
      );
      return;
    }

    await proceedWithUpload(payload);
  };

  const proceedWithUpload = async (payload: UploadRequest) => {
    const response = await uploadDocuments(payload).unwrap();

    if (response.error_files) {
      const errorFiles = response.error_files.map((f) => f.filename).join(", ");
      setErrorModalMssg(
        `${t("The following file(s) could not be uploaded")}: ${errorFiles}`
      );
      setShowErrorModal(true);
    }

    setFiles([]);
    setBeginImport(false);
    setConfirmationModal(false);
    setPendingUploadPayload(null);
    // If a user uploads a file in a dif brand, we need to remove it from the table
    setDocuments((prevDocuments) =>
      prevDocuments.filter((doc) => {
        return doc.uploadStatus !== "Ready";
      })
    );
  };

  const proceedWithUpdateRoles = async (payload: UpdateRolesRequest) => {
    await updateRoles(payload);
  };

  const proceedWithUpdateBrands = async (payload: UpdateBrandsRequest) => {
    await updateBrands(payload);
  };

  const proceedWithUpdateViewable = async (payload: UpdateViewableRequest) => {
    await updateViewable(payload);
  };

  const handleMultipleUpload = async (incomingFiles: File[]) => {
    const addAllBrands = autorizedBrands
      .map((b) => b.toLowerCase())
      .includes("ub");

    const newTempDocuments = incomingFiles.map((file, index) => ({
      id: documents.length + index + 1,
      Filename: file.name,
      Brand: addAllBrands ? brandsArrayForAdminDocumentsAPI : [],
      Role: [],
      active_flag: undefined,
      uploadStatus: "Incomplete",
    }));

    setDocuments((prevDocuments) => [...newTempDocuments, ...prevDocuments]);

    setFiles((prevFiles) => [...incomingFiles, ...prevFiles]);
  };

  const handleSelectedBrands = (payload: WingmanAdminDocument) => {
    const { Filename, Brand, id, uploadStatus } = payload;
    if (uploadStatus === "Complete") {
      proceedWithUpdateBrands(createUpdateBrandsPayload(payload));
      return;
    } else {
      setDocuments((prevDocuments) =>
        prevDocuments.map((doc) => {
          if (doc.Filename === Filename && doc.id === id) {
            const updatedDoc = { ...doc, Brand };
            const uploadStatus =
              updatedDoc.Viewable === undefined
                ? "Incomplete"
                : checkIfAllFieldsAreFilled({
                  Brand: updatedDoc.Brand || [],
                  Role: updatedDoc.Role || [],
                  Viewable: updatedDoc.Viewable,
                });
            return {
              ...updatedDoc,
              uploadStatus: uploadStatus,
            };
          }
          return doc;
        })
      );
    }
  };

  const handleSelectedRoles = (payload: WingmanAdminDocument) => {
    const { Filename, Role, id, uploadStatus } = payload;
    if (uploadStatus === "Complete") {
      proceedWithUpdateRoles(createUpdateRolesPayload(payload));
      return;
    } else {
      setDocuments((prevDocuments) =>
        prevDocuments.map((doc) => {
          if (doc.Filename === Filename && doc.id === id) {
            const updatedDoc = { ...doc, Role };
            const uploadStatus =
              updatedDoc.Viewable === undefined
                ? "Incomplete"
                : checkIfAllFieldsAreFilled({
                  Brand: updatedDoc.Brand || [],
                  Role: updatedDoc.Role || [],
                  Viewable: updatedDoc.Viewable,
                });
            return {
              ...updatedDoc,
              uploadStatus: uploadStatus,
            };
          }
          return doc;
        })
      );
    }
  };

  const handleViewableFlag = (payload: WingmanAdminDocument) => {
    const { Filename, Viewable, id, uploadStatus } = payload;
    if (uploadStatus === "Complete") {
      const updatePayload = createUpdateViewablePayload(payload);
      if (updatePayload) {
        proceedWithUpdateViewable(updatePayload);
      }
      return;
    } else {
      setDocuments((prevDocuments) =>
        prevDocuments.map((doc) => {
          if (
            doc.Filename === Filename &&
            doc.id === id &&
            Viewable !== undefined
          ) {
            const updatedDoc = { ...doc, Viewable: Viewable };
            const uploadStatus = checkIfAllFieldsAreFilled({
              Brand: updatedDoc.Brand ? updatedDoc.Brand : [],
              Role: updatedDoc.Role ? updatedDoc.Role : [],
              Viewable: updatedDoc.Viewable,
            });
            return {
              ...updatedDoc,
              uploadStatus: uploadStatus,
            };
          }
          return doc;
        })
      );
    }
  };

  const handleDelete = async (payload: WingmanAdminDocumentIndentifier) => {
    setFileToDelete(payload.Filename);
    setPayloadOfFileToDelete(payload);
    setShowConfirmDeleteModal(true);
  };

  const proceedWithDelete = async () => {
    if (payloadOfFileToDelete && payloadOfFileToDelete.Filename) {
      await deleteDocument({ Filename: payloadOfFileToDelete.Filename });
      setFileToDelete("");
      setShowConfirmDeleteModal(false);
      setPayloadOfFileToDelete(undefined);
    }
  };

  const handleClear = async (payload: WingmanAdminDocumentIndentifier) => {
    setDocuments((prevDocuments) =>
      prevDocuments.filter((doc) => doc.Filename !== payload.Filename)
    );

    // After we clear we fetch the documents from BE
    refetchDocumentByBrand();
  };

  const handleReplace = async (payload: WingmanAdminDocumentIndentifier) => {
    setFileToReplace({ Filename: payload.Filename, id: payload.id });
    setShowReplaceModal(true);
  };

  const proceedWithReplace = async (payload: DocumentReplaceRequest) => {
    await replaceDocument(payload);
    setReplacingFile(false);
    setFileToReplace(null);
  };

  const handleReplaceUpload = async (file: File) => {
    if (fileToReplace) {
      setFileToReplace((fileData) => {
        return fileData ? { ...fileData, file } : fileData;
      });

      if (fileToReplace.Filename) {
        setConfirmationReplaceModalMssg(
          `Are you sure you want to replace the file ${fileToReplace.Filename} with ${file.name}?`
        );
      }

      setShowConfirmReplaceModal(true);
      setReplacingFile(true);
    }
  };

  const handleModalContinue = async () => {
    setConfirmationModal(false);
    setConfirmationModalMssg("");
    if (pendingUploadPayload) {
      if (pendingUploadPayload.files.length > 0) {
        await proceedWithUpload(pendingUploadPayload);
      }
      return;
    }
  };

  const handleReplaceContinue = async () => {
    setShowConfirmReplaceModal(false);
    setConfirmationReplaceModalMssg("");

    if (replacingFile && fileToReplace && fileToReplace.file) {
      const uploadedFileToReplace = documents.filter(
        (d) => d.Filename === fileToReplace.Filename
      )[0] as WingmanAdminDocumentToReplace;
      await proceedWithReplace(
        createReplacePayload(uploadedFileToReplace, fileToReplace.file)
      );
      return;
    }
  };

  return (
    <>
      <PermissionGuard permissions={[UserPermissions.WingmanSourceAdmin]}>
        <div className="max-w-screen-2xl">
          {!beginImport ? (
            <>
              <Heading>
                <Trans>Wingman Admin</Trans>
              </Heading>
              <p className="text-sm">
                {t`This page will allow you to upload documents to Wingman.`}
              </p>

              <div className="text-sm flex my-6 lg:justify-end align-bottom gap-8 flex-wrap">
                <button onClick={() => handleShowUploadModalMultiple(true)}>
                  <FontAwesomeIcon
                    title={t("Upload multiple documents to Wingman")}
                    icon={faUpload}
                    className="text-unleashed-pink h-6 w-6 hover:saturate-200 transition"
                  />
                </button>
              </div>
            </>
          ) : (
            <WingmanAdminUploadWarning />
          )}
          <div>
            <WingmanAdminUploadTable
              beginImport={beginImport}
              handleClear={handleClear}
              handleDelete={handleDelete}
              handleReplace={handleReplace}
              authorizedBrands={autorizedBrands}
              handleViewableFlag={handleViewableFlag}
              handleSelectedRoles={handleSelectedRoles}
              handleSelectedBrands={handleSelectedBrands}
              uploadDocuments={
                !beginImport
                  ? documents ?? []
                  : (documents ?? []).filter(
                    (d) => d.uploadStatus === UploadStatus.Ready
                  )
              }
            />
          </div>
          <div className="py-6 flex gap-4 lg:justify-end">
            {!beginImport ? (
              <Button
                type="button"
                disabled={
                  !documents?.some((d) => d.uploadStatus === UploadStatus.Ready)
                }
                className={classNames(
                  "inline-flex items-center",
                  documents?.some((d) => d.uploadStatus === UploadStatus.Ready)
                    ? "hover:bg-white hover:text-unleashed-pink transition"
                    : "cursor-not-allowed"
                )}
                onClick={() => handleBeginImport()}
              >
                <Trans>Next</Trans>
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  className={
                    "inline-flex items-center text-unleashed-pink bg-white hover:bg-primary hover:text-white transition"
                  }
                  onClick={() => handleCancel()}
                >
                  <Trans>Cancel</Trans>
                </Button>
                <Button
                  type="button"
                  className={
                    "inline-flex items-center hover:bg-white hover:text-unleashed-pink transition"
                  }
                  onClick={async () => await handleImport()}
                >
                  <Trans>Import</Trans>
                </Button>
              </>
            )}
          </div>

          <MultipleFileUploadModal
            modalHeader={t`Upload to Wingman Multipe`}
            showModal={showUploadModalMultiple}
            handleShowModal={handleShowUploadModalMultiple}
            acceptedFileTypes=".xls, .xlsx, .doc, .docx, .pdf, .mp4, .ppt, .pptx, .txt"
            maxFileSizeBytes={5 * 1024 * 1024 * 1024}
            onSubmit={handleMultipleUpload}
          />

          <FileUploadModal
            modalHeader={t`Replace Document`}
            showModal={showReplaceModal}
            handleShowModal={handleShowReplaceModal}
            acceptedFileTypes=".xls, .xlsx, .doc, .docx, .pdf, .mp4, .ppt, .pptx, .txt"
            maxFileSizeBytes={5 * 1024 * 1024 * 1024}
            onSubmit={handleReplaceUpload}
          />

          <WingmanAdminModal
            showModal={showConfirmationModal}
            handleShowModal={setConfirmationModal}
            modalHeader={t`Confirmation`}
            message={confirmationModalMssg}
            onSubmit={handleModalContinue}
          />

          <WingmanAdminConfirmDeleteModal
            showModal={showConfirmDeleteModal}
            handleShowModal={setShowConfirmDeleteModal}
            modalHeader={t`Confirmation`}
            message={`${t("Are you sure you want to delete")} ${fileToDelete}?`}
            onSubmit={proceedWithDelete}
          />

          <WingmanAdminConfirmReplaceModal
            showModal={showConfirmReplaceModal}
            handleShowModal={setShowConfirmReplaceModal}
            modalHeader={t`Confirmation`}
            message={confirmationReplaceModalMssg}
            onSubmit={handleReplaceContinue}
          />

          <WingmanAdminErrorModal
            showModal={showErrorModal}
            handleShowModal={setShowErrorModal}
            modalHeader={t`Error Uploading`}
            message={errorModalMssg}
            onSubmit={handleErrorContinue}
          />
        </div>
      </PermissionGuard>
    </>
  );
};

